import { Button, Group, Modal, Text } from "@mantine/core";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  getDocumentQuery,
  getDocumentTypesQuery,
  updateDocument,
  type UpdateDocumentRequest,
  type UpdateDocumentResponse
} from "@/api";
import {
  EditDocumentOrNote,
  type EditDocumentOrNoteFormSubmitValues
} from "@/components/EditDocumentOrNote";
import { showError, showSuccess } from "@/notifications/Notifications";
import { canAddOrEditProspectDocuments } from "@/security/logic";

export const Route = createFileRoute(
  "/prospects/$prospectId/documents/$documentId/edit"
)({
  component: DocumentEditComponent,
  beforeLoad: ({ context, location }) => {
    const { userClaim, prospect } = context;

    // redirect from $documentId/edit if user lacks edit permissions
    if (
      prospect &&
      !canAddOrEditProspectDocuments(userClaim, prospect.communityId)
    ) {
      throw redirect({
        to: "/notAuthorized/$",
        params: { _splat: location.href }
      });
    }
  }
});

function DocumentEditComponent() {
  const { prospectId, documentId } = Route.useParams();
  const navigate = useNavigate();
  const { secureAxios } = Route.useRouteContext().apiHook;
  const queryClient = useQueryClient();

  const { data: document, isPending: isDocumentPending } = useQuery(
    getDocumentQuery(secureAxios, documentId)
  );

  const { data: documentTypes = [] } = useQuery(
    getDocumentTypesQuery(secureAxios)
  );

  const handleClose = useCallback(() => {
    void navigate({
      to: "/prospects/$prospectId/documents",
      params: { prospectId }
    });
  }, [navigate, prospectId]);

  const updateDocumentMutation = useMutation<
    UpdateDocumentResponse,
    Error,
    UpdateDocumentRequest
  >({
    mutationFn: async (request) => {
      return await updateDocument(secureAxios, request);
    },
    onSuccess: () => {
      showSuccess("The document has been successfully updated.");
      // Invalidate the documents list query and the individual document query
      void queryClient.invalidateQueries({
        queryKey: ["documents", "prospect", Number(prospectId)]
      });
      void queryClient.invalidateQueries({
        queryKey: ["documents", documentId]
      });
      handleClose();
    },
    onError: (error) => {
      showError(
        error instanceof Error ? error.message : "An unknown error occurred",
        "Error updating document"
      );
    }
  });

  const handleSubmit = useCallback(
    (values: EditDocumentOrNoteFormSubmitValues) => {
      updateDocumentMutation.mutate({
        documentId,
        title: values.title,
        typeId: values.documentTypeId,
        includeDetailsInLivingStory: values.includeInLivingStory,
        detailsHtml: values.detailsHtml,
        file: values.file,
        fileRemoved: values.fileRemoved
      });
    },
    [updateDocumentMutation, documentId]
  );

  if (isDocumentPending) {
    return (
      <Modal
        opened={true}
        closeOnClickOutside={false}
        closeOnEscape={false}
        onClose={handleClose}
        title="Loading..."
        centered
      >
        <Text>Loading document...</Text>
      </Modal>
    );
  }

  if (!document) {
    return (
      <Modal
        opened={true}
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
        onClose={handleClose}
        title="Document Not Found"
        centered
      >
        <Text>The requested document could not be found.</Text>
        <Group justify="end" mt="md">
          <Button onClick={handleClose}>Close</Button>
        </Group>
      </Modal>
    );
  }

  return (
    <EditDocumentOrNote
      document={document}
      documentTypes={documentTypes}
      isSubmitting={updateDocumentMutation.isPending}
      onSubmit={handleSubmit}
      onClose={handleClose}
      title={`Edit: ${document.title}`}
      submitButtonText="Save Changes"
    />
  );
}
